import CircleIcon from "@mui/icons-material/Circle";
import { useCallback, useEffect, useState } from "react";

import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import { WAGE_REPORTS_PAGE_SIZE } from "../../../common/components/functional/pagination/constants";
import { TablePaginationProps } from "../../../common/components/functional/pagination/types";
import useIsLoading from "../../../hooks/useIsLoading";
import { useTablePagination } from "../../../hooks/useTablePagination";
import theme from "../../../theme";
import { WAGE_REPORT_STATUS_VALID } from "../constants";
import { PayslipDetailed } from "../payslips/payslipAPI";
import { PayslipDetails } from "../payslips/PayslipDetails";
import { fetchPayslipById } from "../payslips/payslipsSlice";
import { RowField } from "../payslips/PayslipsTable";
import { SalariesHeader } from "../SalariesHeader";
import { StyledTableContainer } from "../styled";
import { WageReportDeliveryDetailsModal } from "./WageReportDeliveryDetails";
import { WageReportsActionBanner } from "./WageReportsActionBanner";
import { WageReport, WageReportDeliveryDetails } from "./wageReportsAPI";
import {
  fetchWageReportById,
  fetchWageReports,
  selectAll,
} from "./wageReportsSlice";
import { WageReportsTable } from "./WageReportsTable";

type Props = {
  filters: SearchFilter[];
};

export const WageReports = ({ filters }: Props) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const isLoading = useIsLoading(["wageReports"]);
  const wageReports = useAppSelector((state) => selectAll(state));
  const { cursor, hits } = useAppSelector((state) => state.wageReports);

  const [payslipDetailed, setPayslipDetailed] = useState<
    PayslipDetailed | undefined
  >();
  const [isPayslipDetailsModalOpen, setIsPayslipDetailsModalOpen] =
    useState(false);

  const [wageReportDetailed, setWageReportDetailed] = useState<
    WageReportDeliveryDetails | undefined
  >();
  const [isDeliveryDetailsModalOpen, setIsDeliveryDetailsModalOpen] =
    useState(false);
  const [selectedWageReports, setSelectedWageReports] = useState<string[]>([]);

  const rowFields: RowField[] = [
    { name: "", width: "40px" },
    { name: "employer", width: "165px" },
    { name: "talent", width: "170px" },
    { name: "payslip", width: "120px" },
    { name: "latestSentReport", width: "120px" },
    { name: "salaryDate", width: "120px" },
    { name: "statusChanged", width: "165px" },
    { name: "status", width: "60px" },
    { name: "errorCode", width: "120px" },
  ];

  const dispatchSearch = useCallback(
    (cursor?: string | null) => {
      const filterObject = filters.reduce((acc, filter) => {
        const { name, value } = filter;
        return { ...acc, [name]: value };
      }, {});
      dispatch(
        fetchWageReports({
          ...filterObject,
          ...(cursor && { cursor }),
        }),
      );
    },
    [dispatch, filters],
  );

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<WageReport>({
      data: wageReports,
      cursor,
      hits,
      pageSize: parseInt(WAGE_REPORTS_PAGE_SIZE, 10),
      fetchNext: (cursor) => dispatchSearch(cursor),
      preventDispatch: true,
    });

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize: parseInt(WAGE_REPORTS_PAGE_SIZE, 10),
  };

  const rows = pageData.map((item) => {
    return {
      id: item.id,
      fields: [
        {
          name: "employer",
          value: item.eezyCompanyName,
        },
        {
          name: "talent",
          value: item.talentName,
          type: "link",
          width: "170px",
          linkProps: {
            href: `/home/talents/${item.talentId}/employment`,
            target: "_blank",
            rel: "noopener noreferrer",
          },
        },
        {
          name: "payslip",
          value: item.payslipNumber,
          type: "link",
          width: "120px",
          onClick: () => getPayslipDetails(item.payslipId),
        },
        {
          name: "latestSentReport",
          value: t("common:open"),
          type: "link",
          onClick: () => getWageReportDeliveryDetails(item.id),
        },
        {
          name: "salaryDate",
          value: item.salaryDate,
          type: "date",
          width: "120px",
        },
        {
          name: "statusChanged",
          type: "datetime",
          value: item.statusChanged,
        },
        {
          name: "status",
          value: (
            <CircleIcon
              sx={{
                color:
                  item.status === WAGE_REPORT_STATUS_VALID.value
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                width: 15,
                height: 15,
                marginLeft: "20px",
              }}
            />
          ),
          type: "icon",
          width: "60px",
        },
        {
          name: "errorCode",
          value: item.errorCodes?.[0] ?? "",
        },
      ],
    };
  });

  const getPayslipDetails = (id: string) => {
    setIsPayslipDetailsModalOpen(true);
    dispatch(fetchPayslipById({ id, languageCode: i18n.language })).then(
      (action) => {
        if (isFulfilled(action)) {
          setPayslipDetailed(action.payload);
        }
      },
    );
  };

  const getWageReportDeliveryDetails = (id: string) => {
    setIsDeliveryDetailsModalOpen(true);
    dispatch(fetchWageReportById(id)).then((action) => {
      if (isFulfilled(action)) {
        setWageReportDetailed(action.payload);
      }
    });
  };

  const updateSelectedItems = (selectedItemId: string, selected: boolean) => {
    if (selected) {
      setSelectedWageReports([...selectedWageReports, selectedItemId]);
    } else {
      setSelectedWageReports(
        selectedWageReports.filter((id) => id !== selectedItemId),
      );
    }
  };

  useEffect(() => {
    resetPagination();
    dispatchSearch();
  }, [dispatch, resetPagination, dispatchSearch]);

  return (
    <StyledTableContainer>
      <WageReportDeliveryDetailsModal
        wageReport={wageReportDetailed}
        isOpen={isDeliveryDetailsModalOpen}
        onClose={() => {
          setIsDeliveryDetailsModalOpen(false);
          setWageReportDetailed(undefined);
        }}
        onOpenPayslip={getPayslipDetails}
      />
      <PayslipDetails
        payslip={payslipDetailed}
        isOpen={isPayslipDetailsModalOpen}
        onClose={() => {
          setIsPayslipDetailsModalOpen(false);
          setPayslipDetailed(undefined);
        }}
      />
      <SalariesHeader pagination={paginationProps} isLoading={isLoading} />
      <WageReportsTable
        rowFields={rowFields}
        rows={rows}
        isLoading={isLoading}
        selectedItems={selectedWageReports}
        updateSelected={updateSelectedItems}
      />
      <WageReportsActionBanner
        selectedItems={selectedWageReports}
        emptyItems={() => setSelectedWageReports([])}
      />
    </StyledTableContainer>
  );
};
